
import { useAtom, useSetAtom } from "jotai"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import { categoryModalAtom } from "@/atoms"
import { filterItemOffersByName } from "@/lib/filter-item-offers-by-name"
import { searchOpenAtom, searchTermAtom } from "@/atoms/search"
import { FloatingFullscreenButton } from "@/components"
import { FloatingCartButton } from "@/components/cart/FloatingCartButton"
import { HeaderMenu } from "@/components/home"
import { CategoryGrid } from "@/components/home/CategoryGrid"
import { StorefrontSections } from "@/components/home/StorefrontSections"
import { DaypartSuggestionsRow } from "@/components/suggestions/DaypartSuggestionsRow"
import { CouponReveal } from "@/components/marketing/CouponReveal"
import { SchedulingBanner } from "@/components/scheduling/SchedulingBanner"
import { ItemModalContent } from "@/components/item/ItemModalContent"
import { CategoryModal } from "@/components/modals/CategoryModal"
import { ImageModal } from "@/components/modals/ImageModal"
import { ItemModal } from "@/components/modals/ItemModal"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { usePageBackground } from "@/hooks/use-page-background"
import { useItemModalParam } from "@/hooks/use-item-modal-param"
import { useMerchantStatus } from "@/hooks/use-merchant-status"
import LottieComponent from "@/components/lottie-animations/LottieComponent"
import { cn } from "@/lib/cn"
import { BasicInfo, Category, Service } from "@/types"
import { CategoryContent } from "./category-content"
import { CategoryImages } from "./category-images"
import { EmptyState, PageContainer, SearchEmpty, Surface } from "@/components/ui"

type PropsCategoryMenu = {
  basicInfo: BasicInfo
  categories: Category[]
  itemNames: Record<string, string> | undefined
  deliveryService: Service
}

export function CategoryMenu({ basicInfo, categories, itemNames, deliveryService }: PropsCategoryMenu) {
  const {
    visibleItemOffers,
    categoriesItemOffers,
    visibleCategories: availableCategories,
  } = useBrowsingChannelCatalog()
  const setSearchOpen = useSetAtom(searchOpenAtom)
  const { merchantIsOpen } = useMerchantStatus()
  const { cart } = useOptimisticCart()
  const cartItemIds = useMemo(() => [...new Set(cart.items.map((line) => line.itemOffer.itemId))], [cart.items])

  const [filteredItemOffers, setFilteredItemOffers] = useState(visibleItemOffers)
  const [filteredCategories, setFilteredCategories] = useState(availableCategories)
  const [category, setCategory] = useState<Category | undefined>()
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [scrollYPosition, setScrollYPosition] = useState(0)
  const [showSearch, setShowSearch] = useState(false)
  const [itemsFilter, setItemsFilter] = useAtom(searchTermAtom)
  const { itemOfferId, openItem: handleItemClick, closeItem: handleItemModalClose } = useItemModalParam()

  const imageRef = useRef<HTMLDivElement>(null)
  const modalContentRef = useRef<HTMLDivElement>(null)
  const itemModalContentRef = useRef<HTMLDivElement>(null)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const categorySearch = searchParams.get("category")
  const [categoryModal, setCategoryModal] = useAtom(categoryModalAtom)


  useEffect(() => {
    const itemOffers = categoriesItemOffers[categorySearch ?? ""] ?? []

    setCategory(categories.find(c => c.id === categorySearch))
    setFilteredItemOffers(itemOffers)
    setCategoryModal({
      open: !!categorySearch,
      categoryId: categorySearch || undefined
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySearch, setCategoryModal])

  useEffect(() => {
    if (!category || !categoryModal.open) return

    let attachedTo: HTMLDivElement | null = null

    const handleScroll = () => {
      if (attachedTo) setScrollYPosition(attachedTo.scrollTop)
    }

    const timer = setTimeout(() => {
      attachedTo = modalContentRef.current
      attachedTo?.addEventListener('scroll', handleScroll)
    }, 100)

    return () => {
      clearTimeout(timer)
      attachedTo?.removeEventListener('scroll', handleScroll)
    }
  }, [category, categoryModal.open])

  useEffect(() => {
    if (!categoryModal.open) return

    let observer: IntersectionObserver | null = null
    const timer = setTimeout(() => {
      const imageElement = imageRef.current
      const modalContent = modalContentRef.current

      if (!imageElement || !modalContent) return

      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.intersectionRatio > 0.1) {
            setScrollYPosition(modalContent.scrollTop)
          }
        },
        {
          threshold: [0, 0.1, 0.5, 1],
          rootMargin: '-50px 0px -50px 0px'
        }
      )
      observer.observe(imageElement)
    }, 300)

    return () => {
      clearTimeout(timer)
      observer?.disconnect()
    }
  }, [categoryModal.open])

  useEffect(() => {
    const itemOffers = categoriesItemOffers[categorySearch ?? ""] ?? []

    if (itemNames && itemOffers.length > 0) {
      setFilteredItemOffers(filterItemOffersByName(itemOffers, itemNames, itemsFilter))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsFilter])

  useEffect(() => {
    if (!itemsFilter.trim()) {
      setFilteredCategories(availableCategories)
      return
    }

    const filtered = availableCategories.filter(category =>
      category.name.toUpperCase().includes(itemsFilter.toUpperCase()) ||
      (category.description && category.description.toUpperCase().includes(itemsFilter.toUpperCase()))
    )

    setFilteredCategories(filtered)
  }, [itemsFilter, availableCategories])


  usePageBackground()

  const handleImageClick = () => {
    if (category?.image?.URL) {
      setIsImageModalOpen(true);
    }
  };


  function handleModalClose() {
    setShowSearch(false)
    setItemsFilter("")
    setCategoryModal({ open: false, categoryId: undefined })
    navigate("/")
  }

  function clearFilter() {
    setItemsFilter("")
    setSearchOpen(false)
    setShowSearch(false)
  }

  function clearFilterItens() {
    setFilteredItemOffers(visibleItemOffers)
    setSearchOpen(false)
    setShowSearch(false)
  }

  return (
    <>
      <HeaderMenu
        basicInfo={basicInfo}
        deliveryService={deliveryService}
      />
      <CouponReveal />
      <SchedulingBanner />
      <StorefrontSections />
      <DaypartSuggestionsRow cartItemIds={cartItemIds} />
      <Surface surface="page" className="py-4">
        <CategoryGrid>
          {filteredCategories.length > 0 ? (
            filteredCategories.map(c => <CategoryImages key={c.id} category={c} setItemsFilter={setItemsFilter} />)
          ) : itemsFilter.length > 0 ? (
            <SearchEmpty
              className="col-span-full"
              title="Nenhuma categoria encontrada"
              description={`Não encontramos categorias com o termo "${itemsFilter}".`}
              onClear={clearFilter}
            />
          ) : merchantIsOpen ? (
            <EmptyState
              size="section"
              className="col-span-full"
              illustration={<LottieComponent />}
              title="Ainda não temos itens disponíveis para venda"
              description="Estamos preparando nosso cardápio. Volte em breve!"
            />
          ) : null}
        </CategoryGrid>
      </Surface>
      <FloatingCartButton className={cn(categoryModal.open && "hidden")} />
      <PageContainer className="md:hidden">
        <FloatingFullscreenButton />
      </PageContainer>

      <CategoryModal
        open={categoryModal.open}
        contentRef={modalContentRef}
        onClose={handleModalClose}
      >
        {filteredItemOffers && category && (
          <CategoryContent
            category={category}
            handleImageClick={handleImageClick}
            imageRef={imageRef}
            scrollYPosition={scrollYPosition}
            setShowSearch={setShowSearch}
            showSearch={showSearch}
            itemsFilter={itemsFilter}
            setItemsFilter={setItemsFilter}
            filteredItemOffers={filteredItemOffers}
            handleModalClose={handleModalClose}
            handleItemClick={handleItemClick}
            clearFilterItens={clearFilterItens}
          />
        )}
      </CategoryModal>

      <ItemModal
        open={itemOfferId !== ''}
        onClose={handleItemModalClose}
        contentRef={itemModalContentRef}
      >
        <ItemModalContent
          onClose={handleItemModalClose}
          itemOfferId={itemOfferId}
          scrollContainerRef={itemModalContentRef}
        />
      </ItemModal>

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={category?.image?.URL}
        crc={category?.image?.["CRC-32"]}
        alt={category?.name ?? "Imagem da categoria"}
      />
    </>
  )
}
